import { createTheme, Grid, IconButton, MuiThemeProvider, Typography } from '@material-ui/core'
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import { useSnackbar } from 'notistack'
import React, { useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import TextField from '../../../components/Form/TextField'
import AlertDialog from '../../../components/ui/AlertDialog'
import ButtonUI from '../../../components/ui/ButtonUI'
import LoadingUI from '../../../components/ui/LoadingUI'
import { useStyles } from './Style'

type props =  any;


interface IFormInput {
    shedId: any;
    registrationUser: any;
    registrationUserName: any;
}

const RegistrationList: React.FC<props> = ({ control, name, allowAdd, allowDelete }) => {

    const classes = useStyles()
    const { t: tGeneral } = useTranslation("General")
    const { t: tShed } = useTranslation("Shed")
    const { enqueueSnackbar } = useSnackbar()
    const tfRegistrationRef = useRef<HTMLElement>(null)
    const tfUserNameRef = useRef<HTMLElement>(null)
    const [loading, setLoading] = useState(false)

    const { control: formControl, handleSubmit, getValues, reset} = useForm<IFormInput>(
        {
            defaultValues: { shedId: 0, registrationUser: '', registrationUserName: '' }
        }
    )

    const { fields, append, remove } = useFieldArray({ control, name: name })

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

    const columns = [
        {
            name: 'shedId',
            options: {
                display: false,
            }
        },    
        {
            name: 'registrationUser',
            label: tShed('registration'),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "left",
                    }),
                setCellProps: () => ({
                    align: "left"
                }),
            }
        },  
        {
            name: 'registrationUserName',
            label: tShed('userName'),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                    }),
                setCellProps: () => ({
                    align: "center"
                }),
            }
        },        
        {
            name: "",
            options: {
                setCellHeaderProps: () => ({
                    align: "right",
                    }),
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    renderButtons(tableMeta.rowData[0], tableMeta.rowIndex)
                )
            }
        },
    ]

    const renderButtons = (shedId: any, rowIndex: any) => {

        let justAdded = shedId === 0;

        if ((allowAdd && justAdded) || allowDelete)
            return (
                <AlertDialog
                dialogTitle={tGeneral("dialogDeleteTitle")}
                dialogContentText={tGeneral("dialogDeleteContentText")}
                cancelTextButton={tGeneral("cancel")}
                confirmTextButton={tGeneral("confirm")}
                confirmDialogFunction={() => { removeItemFromList(rowIndex) }}
                Button={
                    <IconButton size='small' edge="end" >
                        <DeleteIcon />
                    </IconButton>
                }
            />
            )
        else
            return (<></>)
    }

    const removeItemFromList = (rowIndex: any) => {
        remove(rowIndex);
    }

    const addItemToList = async () => {
        setLoading(true)
        
        var data = getValues();

        if (data.registrationUser === '') {
            enqueueSnackbar(tShed("registrationMustBeInformed"), { variant: 'warning' })
            setLoading(false)
            return
        }

        if (data.registrationUserName === '') {
            enqueueSnackbar(tShed("userNameMustBeInformed"), { variant: 'warning' })
            setLoading(false)
            return
        }

        var existingItem = false
        fields.forEach((field) => {
            if ((field as any).registrationUser === data.registrationUser){
                existingItem = true
            }
        })

        if (existingItem) {
            enqueueSnackbar(tShed("itemAlreadyOnList"), { variant: 'warning' })
            setLoading(false)
        return
        }

        append(data);
            reset();
            setLoading(false);
    }

    const gridOptions = {
        textLabels: {
            body: {
                noMatch: tGeneral('noMatch'),
                toolTip: "",
            },
            pagination: {
                next: tGeneral("next"),
                previous: tGeneral("previous"),
                rowsPerPage: tGeneral("rowsPerPage") + ":",
                displayRows: tGeneral("of"),
            }
        }
    }

    const onSubmit = (data: any) => { }

    return (
        <Grid style={{ marginTop: 20, width: "100%", marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 10 }} >
                {tShed("registrations")}
            </Typography>
            {!allowAdd ? <Grid /> :
            <form onSubmit={handleSubmit(onSubmit)} >   
            
                <Grid container spacing={1} style={{ marginBottom: 10 }}>
                    <Grid item xs={3}>
                        <TextField 
                            label={tShed("registration")} 
                            name="registrationUser" 
                            control={formControl} 
                            fullWidth 
                            inputRef={tfRegistrationRef}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {                                
                                if (e.key === 'Enter') {
                                    addItemToList()
                                    tfRegistrationRef.current?.focus()
                                }
                            }} /> 
                    </Grid>

                    <Grid item xs={7}>
                        <TextField 
                            label={tShed("userName")} 
                            name="registrationUserName" 
                            control={formControl} 
                            fullWidth
                            type="text"
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {                                
                                if (e.key === 'Enter') {
                                    addItemToList()
                                    tfRegistrationRef.current?.focus()
                                }
                            }}
                            inputRef={tfUserNameRef}/> 
                    </Grid>

                    <Grid item xs={1}>
                        <ButtonUI
                            color="primary"
                            onClick={(e: any) => {
                                addItemToList();
                                tfRegistrationRef.current?.focus();
                            }}
                            onKeyDown={(e: any) => {
                                if (e.keyCode === 13) {
                                    addItemToList();
                                    tfRegistrationRef.current?.focus();
                                }
                            }}
                            variant="contained"
                            style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 20, marginLeft: 12   }}>
                            <AddIcon />
                        </ButtonUI>
                    </Grid>
                </Grid>
            </form>
            }
            {loading ? <LoadingUI /> :
                <Grid style={{ marginLeft: 10 }} >
                    <MuiThemeProvider theme={myTheme}>
                        <MUIDataTable
                            title=""
                            data={fields}
                            columns={columns}
                            options={{
                                ...gridOptions,
                                serverSide: false,
                                pagination: false,
                                sort: false,
                                selectToolbarPlacement: "none",
                                filter: false,
                                search: false,
                                download: false,
                                print: false,
                                viewColumns: false,
                                selectableRows: 'none',

                            }} />
                    </MuiThemeProvider>
                </Grid>
            }
        </Grid>
    )
}

export default RegistrationList